import { CheckIcon, MenuIcon, StarFilledIcon, StarIcon, TrashIcon } from "@xola/icons";
import { Button } from "@xola/ui-kit";
import { ColDef } from "ag-grid-community";
import clsx from "clsx";
import { SecondaryButton } from "common";
import { uniqueId } from "lodash";
import React, { useMemo, useState } from "react";
import { DraggableProvided } from "react-beautiful-dnd";
import { RosterViewConfiguration } from "../../../../types/Dashboard";
import { InlineConfirmDeletePopover } from "../../../Common/InlineConfirmDeletePopover";
import { RosterColumnId } from "./helpers";
import { isTableView } from "@/stores/dashboardFilterStore";

type CustomViewMenuItemCallback = (rosterColumnsConfiguration: RosterViewConfiguration) => void;

interface CustomViewMenuItemProps {
    viewConfiguration: RosterViewConfiguration;
    currentTableState: ColDef[] | null;
    isActive: boolean;
    isSaving: boolean;
    isDefaultItem: boolean;
    systemHiddenColumns?: RosterColumnId[];
    draggableProvided?: DraggableProvided;
    onClick: CustomViewMenuItemCallback;
    onDelete: CustomViewMenuItemCallback;
    onSaveItemClick: CustomViewMenuItemCallback;
    onToggleFavorite: CustomViewMenuItemCallback;
}

export const CustomRosterViewMenuItem = ({
    viewConfiguration,
    currentTableState,
    isActive,
    isDefaultItem,
    draggableProvided,
    isSaving,
    systemHiddenColumns,
    onClick,
    onDelete,
    onSaveItemClick,
    onToggleFavorite,
}: CustomViewMenuItemProps) => {
    const [viewId] = useState(uniqueId("custom-roster-view-menu"));
    const isTable = isTableView();

    const handleClick = (callback: CustomViewMenuItemCallback) => (e: any) => {
        e.stopPropagation();
        callback(viewConfiguration);
    };

    const isViewConfigDifferent = useMemo(() => {
        if (!viewConfiguration.columns || !currentTableState) {
            return false;
        }

        if (!isActive) {
            return false;
        }

        const filterSystemHidden = (column: any) => {
            if (isTable) return true;
            if (!systemHiddenColumns) return true;

            return !systemHiddenColumns.includes(column.colId ?? column.id);
        };

        const visibleViewColumns = viewConfiguration.columns.filter(
            (column) => !column.hide && filterSystemHidden(column),
        );
        const visibleTableColumns = currentTableState.filter((column) => !column.hide && filterSystemHidden(column));

        if (visibleViewColumns.length !== visibleTableColumns.length) {
            return true;
        }

        for (const [index, viewColumn] of visibleViewColumns.entries()) {
            const tableColumn = visibleTableColumns[index];

            if (
                viewColumn.id !== tableColumn.colId ||
                viewColumn.sort !== tableColumn.sort ||
                viewColumn.pinned !== tableColumn.pinned
            ) {
                return true;
            }
        }

        return false;
    }, [viewConfiguration, isActive, currentTableState, isTable, systemHiddenColumns]);

    return (
        <div
            id={viewId}
            className={clsx(
                "static flex cursor-pointer items-center justify-between space-x-2 border-b border-b-gray-light p-2 px-3",
                isActive && "bg-primary-lighter",
            )}
            data-testid="custom-column-saved-view"
            onClick={handleClick(onClick)}
        >
            <div className="flex flex-grow items-center space-x-2.5">
                {draggableProvided && (
                    <div {...draggableProvided.dragHandleProps}>
                        <MenuIcon size="medium" />
                    </div>
                )}

                <button type="button" className="ui-roster-favourite" onClick={handleClick(onToggleFavorite)}>
                    {viewConfiguration.favourite ? <StarFilledIcon size="medium" /> : <StarIcon size="medium" />}
                </button>

                <span className="font-bold">{viewConfiguration.name}</span>
            </div>

            {!isDefaultItem && (isViewConfigDifferent || isSaving) && (
                <Button size="small" onClick={handleClick(onSaveItemClick)}>
                    {isSaving ? <CheckIcon /> : "Save"}
                </Button>
            )}

            {!isDefaultItem && (
                <div>
                    <InlineConfirmDeletePopover
                        placement="left"
                        offset={[0, 18]}
                        confirmButtonText="Confirm"
                        onConfirm={handleClick(onDelete)}
                    >
                        <SecondaryButton size="small" type="button">
                            <TrashIcon />
                        </SecondaryButton>
                    </InlineConfirmDeletePopover>
                </div>
            )}
        </div>
    );
};
